import type React from 'react';
import { useContext, useEffect, useMemo } from 'react';
import { useRegistryContext } from '../RegistryProvider';
import { buildInstanceContext } from './buildInstanceContext';
import { AppDependencyGraphContext } from './const';
import type { DependencyGraphContextType, DependencyGraphProviderProps } from './types';

/**
 * App-level dependency graph provider. Used only by AppDependencyGraphProvider
 * today; page-level dep graphs use PageDependencyGraphManagerProvider.
 *
 */
export function DependencyGraphProvider({
  children,
  dependencyGraphStoreRef,
  getEntityListForDependencyGraphProvider,
  shouldUseBuiltDependencies,
  ContextComponent,
  interfacePageId,
  interfacePage,
  notifyExternalDependencies,
}: React.PropsWithChildren<DependencyGraphProviderProps>) {
  const { registry } = useRegistryContext();

  const baseContext = useMemo<DependencyGraphContextType>(() => {
    return buildInstanceContext(getEntityListForDependencyGraphProvider, interfacePage, registry, {
      interfacePageId,
      shouldUseBuiltDependencies,
      notifyExternalDependencies,
    });
  }, [
    getEntityListForDependencyGraphProvider,
    interfacePage,
    registry,
    interfacePageId,
    shouldUseBuiltDependencies,
    notifyExternalDependencies,
  ]);

  useEffect(() => {
    if (dependencyGraphStoreRef) {
      dependencyGraphStoreRef.current = { ...baseContext, ...baseContext.actions };
    }
  }, [dependencyGraphStoreRef, baseContext]);

  return <ContextComponent.Provider value={baseContext}>{children}</ContextComponent.Provider>;
}

export function useAppDependencyGraphProvider() {
  const context = useContext(AppDependencyGraphContext);
  if (!context) {
    throw new Error('AppDependencyGraphProvider must be used to access the dependency graph');
  }
  return context;
}
